import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { GlobalDateField } from '../../../components/GlobalComponents/GlobalDateField';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { RadioOption, GlobalRadioButtons } from '../../../components/GlobalComponents/GlobalRadioButtons';
import ProfilePictureHover from '../../../components/ProfilePictureHover';
import { SalaryRow, UnifiedEmployeeRow, EmployeeRole, fmt } from '../PayrollManagement';

// ─────────────────────────────────────────────────────────────────────────────
// This modal has been rebuilt on top of PrimeReact's <Dialog>, following the
// exact same shell as RegisterStudentModal.tsx: a gradient-icon header
// template, the open/close scale+fade animation, and body content broken
// into translucent white "section cards" each with an icon + uppercase
// label. PrimeIcons (`pi pi-...`) are used throughout instead of the custom
// Ico* SVGs from PayrollManagement — this file no longer imports any icons
// from that file, only the SalaryRow/EmployeeRole types and the fmt helper.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  existing: UnifiedEmployeeRow | null;
  onSaved: () => void;
  onClose: () => void;
  toast: React.RefObject<Toast | null>;
}

interface Employee {
  employee_id: number;
  user_id: number;
  name: string;
  email: string | null;
  gender: string;
  contact_no: string;
  qualification: string | null;
  employee_role: EmployeeRole;
  job_profile: string;
  profile_picture?: string | null;
}

interface FormState {
  employee_id: string;
  employee_role: EmployeeRole | '';
  basic_salary: string;
  hra: string;
  transport_allowance: string;
  medical_allowance: string;
  other_allowance: string;
  pf_type: 'percentage' | 'fixed';
  provident_fund_pct: string;
  provident_fund_amount: string;
  professional_tax: string;
  tds_pct: string;
  effective_from: string;
}

const EMPTY: FormState = {
  employee_id: '',
  employee_role: '',
  basic_salary: '',
  hra: '',
  transport_allowance: '',
  medical_allowance: '',
  other_allowance: '',
  pf_type: 'percentage',
  provident_fund_pct: '0',
  provident_fund_amount: '0',
  professional_tax: '0',
  tds_pct: '0',
  effective_from: new Date().toISOString().substring(0, 10),
};

const PF_TYPE_OPTIONS: RadioOption[] = [
  { value: 'percentage', label: '% Percentage' },
  { value: 'fixed', label: '₹ Fixed amount' },
];

const ROLE_COLORS: Record<EmployeeRole, string> = {
  teacher: '#3b82f6',
  staff: '#8b5cf6',
  principal: '#f59e0b',
};

// ─────────────────────────────────────────────────────────────────────────────
// Role colour pill
// ─────────────────────────────────────────────────────────────────────────────
const RolePill: React.FC<{ role: EmployeeRole }> = ({ role }) => {
  const color = ROLE_COLORS[role] ?? '#6b7280';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 9px',
        borderRadius: 20,
        fontSize: 10,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '.4px',
        background: `${color}18`,
        color,
        border: `1px solid ${color}30`,
      }}
    >
      {role}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Section wrapper — matches the translucent white cards used throughout
// RegisterStudentModal (icon chip + uppercase label + divider, then content).
// ─────────────────────────────────────────────────────────────────────────────
const Section: React.FC<{ icon: string; title: string; titleColor?: string; children: React.ReactNode }> = ({
  icon,
  title,
  titleColor = '#1e3a8a',
  children,
}) => (
  <div
    style={{
      background: 'rgba(255, 255, 255, 0.8)',
      borderRadius: 16,
      padding: 20,
      border: '1px solid rgba(59, 130, 246, 0.1)',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: 8,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          flexShrink: 0,
        }}
      >
        <i className={icon} style={{ fontSize: 15 }} />
      </div>
      <span style={{ color: titleColor, fontWeight: 700, fontSize: 14, letterSpacing: '0.02em', textTransform: 'uppercase' }}>{title}</span>
    </div>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Live salary preview
// ─────────────────────────────────────────────────────────────────────────────
const Preview: React.FC<{ form: FormState }> = ({ form }) => {
  const n = (v: string) => parseFloat(v) || 0;

  const basic = n(form.basic_salary);
  const hra = n(form.hra);
  const transport = n(form.transport_allowance);
  const medical = n(form.medical_allowance);
  const other = n(form.other_allowance);
  const gross = basic + hra + transport + medical + other;

  const pf = form.pf_type === 'fixed' ? n(form.provident_fund_amount) : gross * (n(form.provident_fund_pct) / 100);

  const pt = n(form.professional_tax);
  const tds = gross * (n(form.tds_pct) / 100);
  const net = gross - pf - pt - tds;

  const pfLabel = form.pf_type === 'fixed' ? `PF (₹${form.provident_fund_amount})` : `PF (${form.provident_fund_pct}%)`;

  if (gross === 0) return null;

  const rows = [
    { label: 'Basic', val: basic, color: '#1e3a8a' },
    { label: 'HRA', val: hra, color: '#374151' },
    { label: 'Transport', val: transport, color: '#374151' },
    { label: 'Medical', val: medical, color: '#374151' },
    { label: 'Other allowance', val: other, color: '#374151' },
    { label: 'Gross', val: gross, color: '#111827', bold: true },
    { label: pfLabel, val: -pf, color: '#dc2626' },
    { label: 'Professional tax', val: -pt, color: '#dc2626' },
    { label: `TDS (${form.tds_pct}%)`, val: -tds, color: '#dc2626' },
    { label: 'Net salary', val: net, color: '#1e3a8a', bold: true, large: true },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {rows.map((r) => (
        <div
          key={r.label}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: r.label === 'Gross' || r.label === 'Net salary' ? '1px solid rgba(30,58,138,.12)' : 'none',
            paddingTop: r.label === 'Gross' || r.label === 'Net salary' ? 6 : 0,
            marginTop: r.label === 'Gross' || r.label === 'Net salary' ? 4 : 0,
          }}
        >
          <span style={{ fontSize: (r as any).large ? 14 : 12, color: '#6b7280', fontWeight: r.bold ? 700 : 500 }}>{r.label}</span>
          <span
            style={{
              fontSize: (r as any).large ? 15 : 12,
              fontWeight: r.bold ? 800 : 600,
              color: r.color,
              fontFamily: r.bold ? 'inherit' : 'monospace',
            }}
          >
            {r.val < 0 ? `−${fmt(-r.val)}` : fmt(r.val)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────────────────────────────────────
const PayrollSalaryModal: React.FC<Props> = ({ existing, onSaved, onClose, toast }) => {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState<Employee | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  const isEdit = !!existing;

  // ── Fetch employees ──────────────────────────────────────────────────────
  const fetchEmployees = useCallback(async () => {
    setEmpLoading(true);
    try {
      const res = await apiClient.get('/payroll/employees');
      if (res.data.status === 'success') setEmployees(res.data.data ?? []);
    } catch {
      /* silent */
    } finally {
      setEmpLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isEdit) fetchEmployees();
  }, [isEdit, fetchEmployees]);

  // ── Populate form ────────────────────────────────────────────────────────
  useEffect(() => {
    if (existing) {
      setForm({
        employee_id: String(existing.employee_id),
        employee_role: existing.employee_role,
        basic_salary: String(existing.basic_salary),
        hra: String(existing.hra),
        transport_allowance: String(existing.transport_allowance),
        medical_allowance: String(existing.medical_allowance),
        other_allowance: String(existing.other_allowance),
        pf_type: (existing as any).pf_type ?? 'percentage',
        provident_fund_pct: String(existing.provident_fund_pct),
        provident_fund_amount: String((existing as any).provident_fund_amount ?? '0'),
        professional_tax: String(existing.professional_tax),
        tds_pct: String(existing.tds_pct),
        effective_from: existing.effective_from ?? '',
      });
    } else {
      setForm(EMPTY);
      setSelectedEmp(null);
      setEmpSearch('');
    }
    setErrors({});
  }, [existing]);

  // ── Close with fade/scale animation (mirrors RegisterStudentModal) ──────
  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const handleClose = () => {
    if (saving) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 400);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const selectEmployee = (emp: Employee) => {
    setSelectedEmp(emp);
    setForm((f) => ({ ...f, employee_id: String(emp.employee_id), employee_role: emp.employee_role }));
    setErrors((er) => ({ ...er, employee_id: undefined, employee_role: undefined }));
    setDropdownOpen(false);
    setEmpSearch('');
  };

  const clearEmployee = () => {
    setSelectedEmp(null);
    setForm((f) => ({ ...f, employee_id: '', employee_role: '' }));
  };

  const filteredEmps = empSearch.trim()
    ? employees.filter(
        (e) =>
          e.name.toLowerCase().includes(empSearch.toLowerCase()) ||
          e.job_profile.toLowerCase().includes(empSearch.toLowerCase()) ||
          String(e.employee_id).includes(empSearch)
      )
    : employees;

  // ── Validation ───────────────────────────────────────────────────────────
  const validate = (): boolean => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.employee_id) e.employee_id = 'Select an employee';
    if (!form.employee_role) e.employee_role = 'Required';
    if (!form.basic_salary || isNaN(+form.basic_salary) || +form.basic_salary < 0) e.basic_salary = 'Enter a valid amount';
    if (!form.effective_from) e.effective_from = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const res = await apiClient.post('/payroll/salary', {
        employee_id: +form.employee_id,
        employee_role: form.employee_role,
        basic_salary: +form.basic_salary || 0,
        hra: +form.hra || 0,
        transport_allowance: +form.transport_allowance || 0,
        medical_allowance: +form.medical_allowance || 0,
        other_allowance: +form.other_allowance || 0,
        pf_type: form.pf_type,
        provident_fund_pct: +form.provident_fund_pct || 0,
        provident_fund_amount: +form.provident_fund_amount || 0,
        professional_tax: +form.professional_tax || 0,
        tds_pct: +form.tds_pct || 0,
        effective_from: form.effective_from,
      });
      if (res.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Salary definition saved.', life: 3000 });
        setTimeout(() => {
          onSaved();
        }, 200);
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message, life: 4000 });
      }
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Save failed', life: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const err = (k: keyof FormState) =>
    errors[k] ? <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, marginTop: 2, display: 'block' }}>{errors[k]}</span> : null;

  // Effective-from date <-> string bridge, same pattern RegisterStudentModal
  // uses for dob (GlobalDateField wants a Date, the API wants yyyy-mm-dd).
  const effectiveFromDate = form.effective_from ? new Date(form.effective_from) : null;
  const handleEffectiveFromChange = (value: Date | Date[] | (Date | null)[] | null | undefined) => {
    const d = Array.isArray(value) ? value[0] : value;
    const formatted = d
      ? [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-')
      : '';
    setForm((f) => ({ ...f, effective_from: formatted }));
    setErrors((er) => ({ ...er, effective_from: undefined }));
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 24,
          fontWeight: 700,
          boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
          flexShrink: 0,
          border: '3px solid white',
        }}
      >
        <i className={isEdit ? 'pi pi-pencil' : 'pi pi-wallet'} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', lineHeight: 1.2, marginBottom: 4 }}>
          {isEdit ? 'Edit Salary' : 'Add Salary'}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {isEdit ? `${existing!.employee_role} · Employee #${existing!.employee_id}` : 'Select an employee and define their salary'}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={!isClosing}
      onHide={handleClose}
      header={headerTemplate}
      style={{ width: '90vw', maxWidth: '760px', borderRadius: '24px', overflow: 'hidden' }}
      contentStyle={{
        padding: '18px',
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        maxHeight: 'calc(90vh - 120px)',
        overflowY: 'auto',
        border: '12px solid rgba(255, 255, 255, 0.8)',
        borderRadius: '0 0 24px 24px',
        borderTop: 'none',
      }}
      headerStyle={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '20px 24px',
        borderRadius: '24px 24px 0 0',
        borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
        borderRight: '12px solid rgba(255, 255, 255, 0.8)',
        borderTop: '12px solid rgba(255, 255, 255, 0.8)',
        margin: '-12px -12px 0 -12px',
      }}
      modal
      dismissableMask
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .p-dialog-mask {
          backdrop-filter: blur(4px);
          background-color: rgba(0, 0, 0, 0.25) !important;
          animation: maskFadeIn 0.35s ease;
        }
        .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
        @keyframes maskFadeIn {
          from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
        }
        @keyframes maskFadeOut {
          from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
        }
        .p-dialog {
          animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }
        .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important; }
        @keyframes dialogOpen {
          from { opacity: 0; transform: scale(0.92) translateY(-20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes dialogClose {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to { opacity: 0; transform: scale(0.88) translateY(25px); }
        }
        .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
        .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241, 245, 249, 0.5); border-radius: 10px; }
        .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.5); border-radius: 10px; }
        .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100, 116, 139, 0.7); }
        .psm-emp-row:hover { background: rgba(59, 130, 246, 0.05); }
      `}</style>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* ── Employee section ── */}
        <Section icon="pi pi-user" title="Employee">
          {!isEdit ? (
            <div style={{ position: 'relative' }}>
              {selectedEmp ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    borderRadius: 12,
                    border: '1.5px solid #3b82f6',
                    background: 'rgba(59,130,246,.04)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <ProfilePictureHover
                      src={selectedEmp.profile_picture ?? ''}
                      name={selectedEmp.name}
                      cacheBust={String(selectedEmp.employee_id)}
                    />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{selectedEmp.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                        <RolePill role={selectedEmp.employee_role} />
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>
                          {selectedEmp.job_profile} · ID #{selectedEmp.employee_id}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={clearEmployee}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', borderRadius: 8 }}
                  >
                    <i className="pi pi-times" style={{ fontSize: 14, color: '#9ca3af' }} />
                  </button>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  <GlobalInputField
                    label="Search employee"
                    value={empSearch}
                    onChange={(e) => {
                      setEmpSearch(e.target.value);
                      setDropdownOpen(true);
                    }}
                    placeholder={empLoading ? 'Loading employees…' : 'Search by name, role, or ID'}
                    disabled={empLoading}
                    icon={empLoading ? 'pi pi-spin pi-spinner' : 'pi pi-search'}
                    required
                  />
                  <div onFocus={() => setDropdownOpen(true)} tabIndex={-1} />
                </div>
              )}

              {err('employee_id')}

              {dropdownOpen && !selectedEmp && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    background: 'white',
                    borderRadius: 13,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 8px 30px rgba(0,0,0,.12)',
                    maxHeight: 240,
                    overflowY: 'auto',
                    marginTop: 4,
                  }}
                >
                  {filteredEmps.length === 0 ? (
                    <div style={{ padding: '16px 14px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
                      {empSearch ? 'No employees match.' : 'No employees found.'}
                    </div>
                  ) : (
                    filteredEmps.map((emp) => (
                      <div
                        key={`${emp.employee_role}-${emp.employee_id}`}
                        className="psm-emp-row"
                        onClick={() => selectEmployee(emp)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '9px 14px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f9fafb',
                          transition: 'background .12s',
                        }}
                      >
                        <ProfilePictureHover src={emp.profile_picture ?? ''} name={emp.name} cacheBust={String(emp.employee_id)} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {emp.name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                            <RolePill role={emp.employee_role} />
                            <span style={{ fontSize: 11, color: '#9ca3af' }}>{emp.job_profile}</span>
                            {emp.email && (
                              <span style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>
                                · {emp.email}
                              </span>
                            )}
                          </div>
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', flexShrink: 0 }}>#{emp.employee_id}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 12,
                background: 'rgba(30,58,138,.04)',
                border: '1px solid rgba(30,58,138,.12)',
              }}
            >
              <ProfilePictureHover
                src={existing!.profile_picture ?? ''}
                name={existing!.employee_name ?? `Employee #${existing!.employee_id}`}
                cacheBust={existing!.updated_at ? new Date(existing!.updated_at).getTime().toString() : String(existing!.employee_id)}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                  {existing!.employee_name ?? `Employee #${existing!.employee_id}`}
                </div>
                <div style={{ marginTop: 3 }}>
                  <RolePill role={existing!.employee_role} />
                </div>
              </div>
            </div>
          )}
        </Section>

        {/* ── Earnings section ── */}
        <Section icon="pi pi-money-bill" title="Earnings">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {(
              [
                ['basic_salary', 'Basic Salary'],
                ['hra', 'HRA'],
                ['transport_allowance', 'Transport Allowance'],
                ['medical_allowance', 'Medical Allowance'],
                ['other_allowance', 'Other Allowance'],
              ] as [keyof FormState, string][]
            ).map(([k, label]) => (
              <div key={k}>
                <GlobalInputField
                  label={label}
                  type="number"
                  value={(form as any)[k]}
                  onChange={set(k)}
                  placeholder="0"
                  required={k === 'basic_salary'}
                  icon="pi pi-money-bill"
                />
                {err(k)}
              </div>
            ))}
          </div>
        </Section>

        {/* ── Deductions section ── */}
        <Section icon="pi pi-percentage" title="Deductions" titleColor="#dc2626">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <GlobalRadioButtons
                label="Provident Fund Type"
                options={PF_TYPE_OPTIONS}
                value={form.pf_type}
                onChange={(value) => setForm((f) => ({ ...f, pf_type: value as 'percentage' | 'fixed' }))}
                layout="horizontal"
              />
              <div style={{ marginTop: 12, maxWidth: 220 }}>
                {form.pf_type === 'percentage' ? (
                  <GlobalInputField
                    label="PF Percentage"
                    type="number"
                    value={form.provident_fund_pct}
                    onChange={set('provident_fund_pct')}
                    placeholder="0"
                    icon="pi pi-percentage"
                  />
                ) : (
                  <GlobalInputField
                    label="PF Amount (Fixed / Month)"
                    type="number"
                    value={form.provident_fund_amount}
                    onChange={set('provident_fund_amount')}
                    placeholder="0"
                    icon="pi pi-money-bill"
                  />
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <GlobalInputField
                label="Professional Tax"
                type="number"
                value={form.professional_tax}
                onChange={set('professional_tax')}
                placeholder="0"
                icon="pi pi-money-bill"
              />
              <GlobalInputField label="TDS %" type="number" value={form.tds_pct} onChange={set('tds_pct')} placeholder="0" icon="pi pi-percentage" />
            </div>
          </div>
        </Section>

        {/* ── Effective date section ── */}
        <Section icon="pi pi-calendar" title="Effective Date">
          <div style={{ maxWidth: 220 }}>
            <GlobalDateField
              label="Effective From"
              value={effectiveFromDate}
              onChange={handleEffectiveFromChange}
              placeholder="Select date"
              required
              dateFormat="dd/mm/yy"
              showButtonBar
            />
            {err('effective_from')}
          </div>
        </Section>

        {/* ── Live preview section ── */}
        {(() => {
          const gross =
            (parseFloat(form.basic_salary) || 0) +
            (parseFloat(form.hra) || 0) +
            (parseFloat(form.transport_allowance) || 0) +
            (parseFloat(form.medical_allowance) || 0) +
            (parseFloat(form.other_allowance) || 0);
          if (gross === 0) return null;
          return (
            <Section icon="pi pi-calculator" title="Salary Preview">
              <Preview form={form} />
            </Section>
          );
        })()}

        {isEdit && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              background: 'rgba(245,158,11,.07)',
              border: '1px solid rgba(245,158,11,.2)',
              fontSize: 12,
              color: '#b45309',
              fontWeight: 500,
            }}
          >
            <i className="pi pi-exclamation-triangle" style={{ marginRight: 8 }} />
            Saving will deactivate the current salary and create a new revision — old payroll records are unaffected.
          </div>
        )}

        {/* ── Submit ── */}
        <div style={{ textAlign: 'center', marginTop: 8, display: 'flex', justifyContent: 'center', gap: 10 }}>
          <button
            onClick={handleClose}
            disabled={saving}
            style={{
              padding: '10px 20px',
              borderRadius: 10,
              border: '1.5px solid #cbd5e1',
              background: 'white',
              color: '#64748b',
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <GlobalButton
            label={saving ? 'Saving…' : isEdit ? 'Save Revision' : 'Add Salary'}
            onClick={handleSave}
            variant="primary"
            size="medium"
            disabled={saving}
            loading={saving}
            fullWidth={false}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default PayrollSalaryModal;